/**
 * Application Constants
 * Centralized configuration for API URLs, Supabase, etc.
 *
 * Per-environment values come from `Constants.expoConfig.extra`,
 * populated by `mobile/app.config.ts` from `process.env` at config-
 * eval time. This avoids the babel inlining used for `EXPO_PUBLIC_*`
 * vars, which has unpredictable shell-vs-`.env` precedence under
 * Expo SDK 55+. See `app.config.ts` for the schema + defaults.
 *
 * Storybook flags (`EXPO_PUBLIC_STORYBOOK_HEADLESS`) stay as
 * `EXPO_PUBLIC_*` because they're set per-launch via npm scripts,
 * never live in a `.env` file, and the precedence bug doesn't bite.
 */

import { Platform } from "react-native";
import Constants from "expo-constants";
import * as Application from "expo-application";
import type { FluenciaExtra } from "../../app.config";

const extra = (Constants.expoConfig?.extra ?? {}) as Partial<FluenciaExtra>;

/**
 * Returns the dev machine's host address, derived from Metro bundler.
 * Works across all platforms:
 *   - iOS simulator:    localhost (or LAN IP via hostUri)
 *   - Android emulator: 10.0.2.2 (special alias for host machine)
 *   - Physical devices: Mac's LAN IP (via hostUri)
 *
 * Used by: API URL, Sentry Spotlight, Storybook websocket
 */
export const getDevHost = (): string => {
  const hostUri = Constants.expoConfig?.hostUri;
  if (hostUri) {
    return hostUri.split(":")[0];
  }
  if (Platform.OS === "android") {
    return "10.0.2.2";
  }
  return "localhost";
};

// Backend API Configuration
const ENV_API_URL = extra.apiUrl;

// Backend API port (same machine as Metro bundler)
const API_PORT = 3000;

export const getApiUrl = () => {
  if (__DEV__) {
    // Dev: adapt localhost to Metro-derived host
    // (works on simulators, emulators, and physical devices)
    const host = getDevHost();
    if (ENV_API_URL) {
      // .replace is a no-op if URL doesn't contain "localhost"
      return ENV_API_URL.replace("localhost", host);
    }
    return `http://${host}:${API_PORT}/api`;
  }

  // Production / staging: use env var as-is
  return ENV_API_URL || `http://localhost:${API_PORT}/api`;
};

export const API_URL = getApiUrl();

// Supabase Configuration
const ENV_SUPABASE_URL = extra.supabaseUrl ?? "";

/**
 * Same dev-host rewrite as `getApiUrl`, for the same reason.
 *
 * Auth can now point at a LOCAL Supabase stack (`supabase start`, E2E and the
 * website flows — specs/e2e/RUN_LOOP.md §1b), and that stack publishes on the
 * DEV MACHINE's loopback, which is not the device's: on an Android emulator
 * `127.0.0.1` is the emulator, and on a physical device it is the phone. Metro
 * is the only thing that knows where the dev machine actually is.
 *
 * Both spellings, because `supabase status` prints `127.0.0.1` while the rest
 * of the dev config says `localhost`. A cloud URL contains neither, so this is
 * a no-op for staging and production — and it is `__DEV__`-gated regardless.
 */
export const getSupabaseUrl = (): string => {
  if (!__DEV__) return ENV_SUPABASE_URL;
  const host = getDevHost();
  return ENV_SUPABASE_URL.replace("localhost", host).replace("127.0.0.1", host);
};

export const SUPABASE_URL = getSupabaseUrl();
export const SUPABASE_PUBLISHABLE_KEY = extra.supabasePublishableKey ?? "";

// App Configuration
export const APP_NAME = "Fluencia";
/** Native binary version (Info.plist / build.gradle), with Expo config fallback. */
export const APP_VERSION =
  Application.nativeApplicationVersion ??
  Constants.expoConfig?.version ??
  "0.0.0";
export const PRIVACY_POLICY_URL = "https://fluencia.io/privacy";
export const TERMS_OF_SERVICE_URL = "https://fluencia.io/terms";

// Azure Speech Configuration
export const AZURE_SPEECH_REGION = extra.azureSpeechRegion ?? "westeurope";
export const SPEECH_TOKEN_EXPIRY_SECONDS = 600; // 10 minutes

// RevenueCat Configuration
//
// RevenueCat issues one PUBLIC SDK key per platform app — `appl_…` for iOS,
// `goog_…` for Android — so a single shared key cannot serve both. Configuring
// against the wrong platform's key does NOT fail loudly: the SDK connects to
// the other app, finds no entitlements, and every purchaser silently reads as
// free. Resolving it here keeps callers unaware of the platform split.
//
// Falls back to the shared key, which is how the cross-platform Test Store key
// (`test_…`) keeps working, and how any environment that sets neither
// platform-specific var behaves exactly as it did before.
export const REVENUECAT_API_KEY =
  Platform.select({
    ios: extra.revenueCatApiKeyIos,
    android: extra.revenueCatApiKeyAndroid,
  }) ??
  extra.revenueCatApiKey ??
  "test_slaGMIYAYiFYhnuTIdVdFyczwLz";
export const REVENUECAT_ENTITLEMENT_ID = "Fluencia Pro";
export const REVENUECAT_PRODUCT_IDS = {
  monthly: "monthly",
  yearly: "yearly",
} as const;

// E2E Testing Configuration
// When true: disables password autofill (prevents iOS "Save Password?" dialog)
// Dev menu suppression is handled separately via __DEV__ check in App.tsx
export const E2E_MODE: boolean = extra.e2eMode ?? false;

// Audio Lessons (podcast) feature switch — shelved off by default (ADR-032).
// Gates the Listen tab, AudioSeries/AudioPlayer routes, and the lesson
// "Listen on the go" button. Re-enable per build: `AUDIO_LESSONS=true`.
// Backend audio endpoints/seeds stay live regardless.
export const AUDIO_LESSONS_ENABLED: boolean = extra.audioLessonsEnabled ?? false;

// Storybook Configuration — stays as EXPO_PUBLIC_* because metro.config.js
// reads it before app.config.ts evaluates (different layer entirely).
export const STORYBOOK_HEADLESS = process.env.EXPO_PUBLIC_STORYBOOK_HEADLESS === "true";
